import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import TeamV2Sidenav from '../components/dashboard/TeamV2Sidenav';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import TeamV2MembersContainer from './TeamV2MembersContainer';
import TeamV2SubteamsContainer from './TeamV2SubteamsContainer';
import TeamV2TemplatesContainer from './TeamV2TemplatesContainer';
import TeamV2LeadsContainer from './TeamV2LeadsContainer';
import TeamV2DevicesContainer from './TeamV2DevicesContainer';
import TeamV2InsightsContainer from './TeamV2InsightsContainer';
import TeamV2IntegrationsContainer from './TeamV2IntegrationsContainer';
import TeamV2NotificationsContainer from './TeamV2NotificationsContainer';
import TeamV2SettingsContainer from './TeamV2SettingsContainer';
import TeamV2AddMemberContainer from './TeamV2AddMemberContainer';
import { TeamV2MemberEditDashboard } from '../components/editMember';

const TeamV2Dashboard = () => {
    const { authState } = useAuth(); // Use authState instead of userState
    const location = useLocation();
    const navigate = useNavigate();

    // State to track the selected menu
    const [selectedMenu, setSelectedMenu] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // Check authentication and redirect if not authenticated
    useEffect(() => {
        if (!authState.isAuthenticated) {
            navigate('/sign-in');
            return;
        }

        // Add a small delay to ensure all context updates are processed
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, [authState.isAuthenticated, navigate]);



    // Effect to check the current URL and set the selected menu accordingly
    useEffect(() => {
        if (location.pathname.includes('/team-v2/members')) {
            setSelectedMenu('members');
        } else if (location.pathname.includes('/team-v2/subteams')) {
            setSelectedMenu('subteams');
        } else if (location.pathname.includes('/team-v2/templates')) {
            setSelectedMenu('templates');
        } else if (location.pathname.includes('/team-v2/leads')) {
            setSelectedMenu('leads');
        } else if (location.pathname.includes('/team-v2/devices')) {
            setSelectedMenu('devices');
        } else if (location.pathname.includes('/team-v2/insights')) {
            setSelectedMenu('insights');
        } else if (location.pathname.includes('/team-v2/integrations')) {
            setSelectedMenu('integrations');
        } else if (location.pathname.includes('/team-v2/notifications')) {
            setSelectedMenu('notifications');
        } else if (location.pathname.includes('/team-v2/settings')) {
            setSelectedMenu('settings');
        } else {
            setSelectedMenu('');
        }
    }, [location.pathname]);

    // Menu click handler
    const onMenuClick = (menu) => {
        setSelectedMenu(menu); // Update the selected menu state

        switch (menu) {
            case 'members':
                navigate('/team-v2/members');
                break;
            case 'subteams':
                navigate('/team-v2/subteams');
                break;
            case 'templates':
                navigate('/team-v2/templates');
                break;
            case 'leads':
                navigate('/team-v2/leads');
                break;
            case 'devices':
                navigate('/team-v2/devices');
                break;
            case 'insights':
                navigate('/team-v2/insights');
                break;
            case 'integrations':
                navigate('/team-v2/integrations');
                break;
            case 'notifications':
                navigate('/team-v2/notifications');
                break;
            case 'settings':
                navigate('/team-v2/settings');
                break;
            default:
                navigate('/team-v2');
        }
    };

    // CRITICAL FIX: Add key to force re-mount of InsightsContainer when navigating back
    const getComponentKey = (path) => {
        // Create a unique key combining the path and a timestamp for certain components that need clean remounting
        if (path.includes('/insights')) {
            // Include userId to ensure clean remount when user changes
            const userIdPart = authState.user?.userId || 'guest';
            // Use a stable key to avoid unnecessary remounts, but add a dynamic part to force remount when navigating back
            return `insights-${userIdPart}-${Date.now()}`;
        }
        return path; // Default key for other components
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <TeamV2Sidenav onMenuClick={onMenuClick} selectedMenu={selectedMenu} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    backgroundColor: '#f7f7f7',
                    minHeight: '100vh',
                    boxSizing: 'border-box',
                }}
            >
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Routes>
                        <Route path="members" element={<TeamV2MembersContainer user={authState.user} />} />
                        <Route path="subteams" element={<TeamV2SubteamsContainer user={authState.user} />} />
                        <Route path="templates" element={<TeamV2TemplatesContainer user={authState.user} />} />
                        <Route path="leads" element={<TeamV2LeadsContainer user={authState.user} />} />
                        <Route path="devices" element={<TeamV2DevicesContainer user={authState.user} />} />
                        <Route path="insights" element={<TeamV2InsightsContainer user={authState.user} />} />
                        <Route path="integrations" element={<TeamV2IntegrationsContainer user={authState.user} />} />
                        <Route path="notifications" element={<TeamV2NotificationsContainer user={authState.user} />} />
                        <Route path="settings" element={<TeamV2SettingsContainer user={authState.user} />} />
                        <Route path="add-member" element={<TeamV2AddMemberContainer user={authState.user} />} />
                        <Route path="members/edit/:memberId" element={<TeamV2MemberEditDashboard user={authState.user} />} />

                        {/* Fallback route - default to members */}
                        <Route path="*" element={<TeamV2MembersContainer user={authState.user} />} />
                    </Routes>
                )}
                <Outlet />
            </Box>
        </Box>
    );
};

export default TeamV2Dashboard;
